/**
 * Scroll Animations & Page Transitions
 * Simple scroll-triggered fade animations using Intersection Observer
 * Plus page fade transitions on navigation
 */

(function() {
  // Read settings from JSON script tag
  const settingsEl = document.getElementById('scroll-animation-settings');
  if (!settingsEl) return;

  let settings;
  try {
    settings = JSON.parse(settingsEl.textContent);
  } catch (e) {
    console.warn('Failed to parse scroll animation settings');
    return;
  }

  const { animationType, stagger, pageFade } = settings;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesignMode = document.documentElement.classList.contains('shopify-design-mode');

  // ==========================================================================
  // Page Fade Transition
  // ==========================================================================

  if (pageFade && !prefersReducedMotion && !isDesignMode) {
    // Add page fade attribute to body
    document.body.setAttribute('data-page-fade', '');

    // A document restored from the browser's back/forward cache can retain the
    // outgoing fade class and its opacity: 0 animation state. Clear that state
    // both before the page is cached and whenever it is shown again.
    const resetPageFade = () => {
      document.body.classList.remove('is-leaving');
    };

    resetPageFade();
    window.addEventListener('pagehide', resetPageFade);
    window.addEventListener('pageshow', resetPageFade);

    // Handle link clicks for fade out
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');

      // Skip if not a real navigation
      if (!href ||
          href.startsWith('#') ||
          href.startsWith('javascript:') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:') ||
          link.hasAttribute('target') ||
          link.hasAttribute('download') ||
          e.ctrlKey || e.metaKey || e.shiftKey) {
        return;
      }

      // Skip external links
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
      } catch {
        return;
      }

      // Trigger fade out
      e.preventDefault();
      document.body.classList.add('is-leaving');

      setTimeout(() => {
        window.location.href = href;
      }, 300);
    });
  }

  // ==========================================================================
  // Scroll Animations
  // ==========================================================================

  if (!animationType || prefersReducedMotion || isDesignMode) {
    return;
  }

  // Get main content area
  const mainContent = document.getElementById('MainContent');
  if (!mainContent) return;

  // Apply data attributes to all shopify-section elements inside main content
  const sections = mainContent.querySelectorAll('.shopify-section');

  // Track sections that need stagger animation triggered after setup
  const sectionsToAnimate = [];

  sections.forEach((section) => {
    const rect = section.getBoundingClientRect();
    const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

    // Skip animation for sections containing Klaviyo forms - they need to be visible for Klaviyo to render
    const hasKlaviyoForm = section.querySelector('[class*="klaviyo-form-"]');
    if (hasKlaviyoForm) {
      return; // Don't apply scroll animation to this section
    }

    // Set animation type attribute
    section.setAttribute('data-scroll-animation', animationType);

    // Add stagger animation delays to child elements
    const resourceItems = section.querySelectorAll('.resource-list > .resource-list__item');
    resourceItems.forEach((item, index) => {
      item.style.animationDelay = `${index * stagger}ms`;
    });

    const groupBlocks = section.querySelectorAll('.group-block-content > .group-block');
    groupBlocks.forEach((block, index) => {
      block.style.animationDelay = `${index * stagger}ms`;
    });

    const imageHoverItems = section.querySelectorAll('[class*="1-image-hover-grid"] > [class*="1-image-hover-group"]');
    imageHoverItems.forEach((item, index) => {
      item.style.animationDelay = `${index * stagger}ms`;
    });

    const productGridItems = section.querySelectorAll('.product-grid > .product-grid__item');
    productGridItems.forEach((item, index) => {
      item.style.animationDelay = `${index * stagger}ms`;
    });

    // Handle section visibility
    if (isInViewport) {
      // Queue for stagger animation
      sectionsToAnimate.push(section);
    } else {
      // Add hidden class only for below-the-fold sections
      section.classList.add('scroll-animate-hidden');
    }
  });

  // Reveal a section: always remove scroll-animate-hidden alongside adding
  // is-visible. Both classes carry an opacity:0 / opacity:1 rule at identical
  // specificity for the same [data-scroll-animation] selector, so leaving
  // scroll-animate-hidden in place after reveal is a latent race depending on
  // browser cascade tie-breaking. Removing it makes the visible state
  // unambiguous regardless of engine behavior.
  function reveal(section) {
    section.classList.remove('scroll-animate-hidden');
    section.classList.add('is-visible');
  }

  // Trigger stagger animation for above-the-fold sections after a frame
  // This allows the hidden styles to be applied first, then animate in
  if (sectionsToAnimate.length > 0) {
    requestAnimationFrame(() => {
      sectionsToAnimate.forEach((section) => {
        reveal(section);
      });
    });
  }

  // Track sections that enter viewport together for stagger effect
  let pendingAnimations = [];
  let animationFrame = null;

  function processAnimations() {
    pendingAnimations.forEach((el, index) => {
      setTimeout(() => {
        reveal(el);
      }, index * stagger);
    });
    pendingAnimations = [];
    animationFrame = null;
  }

  // Create Intersection Observer
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          pendingAnimations.push(entry.target);
          observer.unobserve(entry.target);
        }
      });

      // Process all intersecting entries together with stagger
      if (pendingAnimations.length > 0 && !animationFrame) {
        animationFrame = requestAnimationFrame(processAnimations);
      }
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }
  );

  // Observe all sections with animation (except those already marked visible)
  document.querySelectorAll('[data-scroll-animation]:not(.is-visible)').forEach((el) => {
    observer.observe(el);
  });

  // Re-observe after Shopify section events
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-scroll-animation]:not(.is-visible)').forEach((el) => {
      observer.observe(el);
    });
  });

  // Safety net: if any section somehow never enters the viewport (e.g. a
  // scroll-jacking widget elsewhere on the page prevents a natural scroll
  // event from ever firing), force every remaining hidden section visible
  // after a fixed timeout so content is never permanently invisible.
  window.setTimeout(() => {
    document.querySelectorAll('[data-scroll-animation].scroll-animate-hidden:not(.is-visible)').forEach((el) => {
      reveal(el);
    });
  }, 4000);
})();
